import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useTenant } from './TenantContext';
import useAuthStore from '../store/authStore';

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

/**
 * Platform-level PostHog analytics provider.
 * Initializes from environment variables (not per-tenant config).
 * Used by the platform owner to understand product usage.
 */
export function PostHogProvider({ children }) {
  const { tenant } = useTenant();
  const { user, isAuthenticated } = useAuthStore();
  const initializedRef = useRef(false);

  // Initialize PostHog once if API key is configured
  useEffect(() => {
    if (POSTHOG_API_KEY && !initializedRef.current) {
      posthog.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: true,
        persistence: 'localStorage',
        loaded: () => {
          initializedRef.current = true;
        },
      });
    }
  }, []);

  // Identify user when authenticated
  useEffect(() => {
    if (!initializedRef.current) return;

    if (isAuthenticated && user) {
      posthog.identify(user._id, {
        email: user.email,
        name: user.name,
        role: user.role,
        tenantId: tenant?._id,
        tenantName: tenant?.name,
      });
    } else {
      posthog.reset();
    }
  }, [isAuthenticated, user, tenant]);

  return children;
}

/**
 * Track a custom event. No-op if PostHog isn't initialized.
 */
export function trackEvent(eventName, properties = {}) {
  if (posthog.__loaded) {
    posthog.capture(eventName, properties);
  }
}
